import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, useApiQuery } from "../../api";
import type { Timesheet } from "../../api";
import { Skeleton } from "../../components";
import { AsyncBoundary, PageHeader, ROUTES } from "../../shell";
import { FeuilleFiche } from "./FeuilleFiche";
import { libelleMois, parseMois } from "./mois";
import styles from "./Feuilles.module.css";

export type FeuillePageProps = {
  /** L'espace où la fiche est montée : décide des liens de retour. */
  espace: "entreprise" | "esn";
};

/**
 * La fiche d'une feuille pour un valideur. Charge la feuille, puis la laisse
 * vivre localement : une décision remplace la feuille rendue par le serveur,
 * sans relecture.
 */
export function FeuillePage({ espace }: FeuillePageProps) {
  const { sheetId = "" } = useParams();
  const chargement = useApiQuery((o) => api.getTimesheet(sheetId, o), [sheetId]);
  const [feuille, setFeuille] = useState<Timesheet | null>(null);

  useEffect(() => {
    setFeuille(chargement.data);
  }, [chargement.data]);

  const retour = espace === "esn" ? ROUTES.esnFeuilles : ROUTES.feuilles;
  const lienMissions = espace === "esn" ? null : ROUTES.missions;

  return (
    <div className={styles.page}>
      <Link to={retour} className={styles.filAriane}>
        ← Feuilles de temps
      </Link>
      <AsyncBoundary
        state={chargement}
        errorTitle="Feuille illisible"
        skeleton={
          <div aria-busy="true">
            <Skeleton variant="title" width="20rem" />
            <Skeleton lines={4} />
          </div>
        }
      >
        {(chargee) => {
          const courante = feuille ?? chargee;
          const mois = parseMois(courante.period_month);
          return (
            <>
              <PageHeader
                eyebrow={espace === "esn" ? `${courante.company_name} · Feuille de temps` : "Feuille de temps"}
                title={`${courante.mission_label}, ${mois === null ? courante.period_month : libelleMois(mois)}`}
                description={`Déclarée par ${courante.consultant_email}.`}
              />
              <FeuilleFiche feuille={courante} onDecision={setFeuille} lienMissions={lienMissions} />
            </>
          );
        }}
      </AsyncBoundary>
    </div>
  );
}
